import {
    AddressForm,
    Alert,
    CheckBox,
    Container,
    Form,
    FormFullPhoneNumber,
    FormInput,
    getPageValidationInfo,
    NameForm,
    useSizes,
} from 'metrix-common3';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const ProfileInsured = (props) => {
    const sizes = useSizes();

    const [sameMailingAddress, setSameMailingAddress] = useState(false);

    // componentDidMount equivalent
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional - mimics componentDidMount behavior
    useEffect(() => {
        props.doGetPolicy();

        const pageHasTooltipInfo =
            props.tooltipInfo?.pages?.length && props.tooltipInfo.pages.some((p) => p.code === props.pageCode);
        if (!pageHasTooltipInfo) {
            props.doGetTooltipInfo(props.policy.product);
        }
    }, []);

    const saveAddressHandler = useCallback(() => {
        props.doUpsertPolicy();
    }, [props]);

    const { error, warning } = getPageValidationInfo(props.pageCode, props.validationInfo);

    return (
        <React.Fragment>
            {(error.message.length !== 0 || warning.message.length !== 0) && (
                <Container top={sizes.md}>
                    {error.message.length !== 0 && <Alert mode='danger' header={error.title} items={error.message} />}
                    {warning.message.length !== 0 && (
                        <Alert mode='warning' header={warning.title} items={warning.message} />
                    )}
                </Container>
            )}
            <NameForm
                pageCode={props.pageCode}
                sectionCode='applicantDetails'
                saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                tooltipInfo={props.tooltipInfo}
                validationInfo={props.validationInfo}
                header={<FormattedMessage id='Profile_Insured.applicantDetails' />}
                label={<FormattedMessage id='Profile_Insured.name' />}
                name={props.policy?.participants?.insured?.party?.name}
                person={props.policy?.participants?.insured?.person}
                saveEvent={props.doUpsertPolicy}
                cancelEvent={props.doGetPolicy}
                reference={props.reference}
                changeEvent={(dotProp, value) =>
                    props.doReducePolicyElement({
                        code:
                            dotProp === 'person.gender'
                                ? `participants.insured.${dotProp}`
                                : `participants.insured.party.name.${dotProp}`,
                        value,
                    })
                }
                allowEdit={props.allowEdit}
            />
            <Form
                pageCode={props.pageCode}
                sectionCode='communicationDetails'
                saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                tooltipInfo={props.tooltipInfo}
                validationInfo={props.validationInfo}
                header={<FormattedMessage id='Profile_Insured.communicationDetails' />}
                label={<FormattedMessage id='Profile_Insured.contactDetails' />}
                saveEvent={props.doUpsertPolicy}
                cancelEvent={props.doGetPolicy}
                allowEdit={props.allowEdit}
            >
                <FormInput
                    fieldCode='email'
                    label={<FormattedMessage id='Profile_Insured.email' />}
                    value={props.policy?.participants?.insured?.party?.communication?.email?.address}
                    type='email'
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'participants.insured.party.communication.email.address',
                            value,
                        })
                    }
                    allowEdit
                />
                <FormFullPhoneNumber
                    fieldCode='fullNumber'
                    label={<FormattedMessage id='Profile_Insured.phone' />}
                    value={props.policy?.participants?.insured?.party?.communication?.phone?.fullNumber}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'participants.insured.party.communication.phone.fullNumber',
                            value,
                        })
                    }
                    allowEdit
                />

                <FormFullPhoneNumber
                    fieldCode='mobileFullNumber'
                    label={<FormattedMessage id='Profile_Insured.mobile' />}
                    value={props.policy?.participants?.insured?.party?.communication?.mobile?.fullNumber}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'participants.insured.party.communication.mobile.fullNumber',
                            value,
                        })
                    }
                    allowEdit
                />
            </Form>
            {!props.productSettings?.hasCustomAddressLookup && (
                <AddressForm
                    {...props}
                    enableAddressSearch
                    pageCode={props.pageCode}
                    sectionCode='applicantAddressDetails'
                    saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                    reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                    tooltipInfo={props.tooltipInfo}
                    validationInfo={props.validationInfo}
                    addressSearchCountryCode={`${props.policy.productCountryCode ? props.policy.productCountryCode : 'USA'}`}
                    header={<FormattedMessage id='Profile_Insured.addressDetails' />}
                    label={<FormattedMessage id='Profile_Insured.locationDetails' />}
                    saveEvent={saveAddressHandler}
                    cancelEvent={props.doGetPolicy}
                    reference={props.reference}
                    changeEvent={(dotProp, value) => {
                        dotProp === 'address'
                            ? props.doReducePolicyElement({ code: 'participants.insured.party.address', value })
                            : props.doReducePolicyElement({
                                  code: `participants.insured.party.address.${dotProp}`,
                                  value,
                              });
                    }}
                    allowEdit={props.allowEdit}
                    isAddressAutofillEnforce={props.productSettings?.isAddressAutofillEnforce}
                    address={props.policy?.participants?.insured?.party?.address}
                />
            )}
            <CheckBox
                label={<FormattedMessage id='Profile_Insured.mailingAddressSameAsAbove' />}
                value={sameMailingAddress}
                clickEvent={() => {
                    props.doReducePolicyElement({
                        code: 'participants.insured.party.mailingAddress',
                        value: props.policy.participants.insured.party.address,
                    });
                    setSameMailingAddress(!sameMailingAddress);
                    props.doUpsertPolicy();
                }}
                left={sizes.sm}
                bottom={sizes.md}
                top={sizes.md}
            />

            {/*Only for tenant*/}
            {props.policy?.participants?.insured?.previousAddress && (
                <AddressForm
                    {...props}
                    enableAddressSearch
                    pageCode={props.pageCode}
                    sectionCode='mailingAddress'
                    saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                    reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                    tooltipInfo={props.tooltipInfo}
                    validationInfo={props.validationInfo}
                    addressSearchCountryCode={`${props.policy.productCountryCode ? props.policy.productCountryCode : 'USA'}`}
                    header={<FormattedMessage id='Profile_Insured.previousAddressDetails' />}
                    label={<FormattedMessage id='Profile_Insured.locationDetails' />}
                    saveEvent={props.doUpsertPolicy}
                    cancelEvent={props.doGetPolicy}
                    reference={props.reference}
                    changeEvent={(dotProp, value) => {
                        dotProp === 'address'
                            ? props.doReducePolicyElement({ code: 'participants.insured.party.address', value })
                            : props.doReducePolicyElement({
                                  code: `participants.insured.previousAddress.${dotProp}`,
                                  value,
                              });
                    }}
                    allowEdit={props.allowEdit}
                    address={props.policy?.participants?.insured?.previousAddress}
                />
            )}
            {!sameMailingAddress && props.policy?.participants?.insured?.party && (
                <AddressForm
                    {...props}
                    enableAddressSearch
                    pageCode={props.pageCode}
                    sectionCode='mailingAddress'
                    saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                    reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                    tooltipInfo={props.tooltipInfo}
                    validationInfo={props.validationInfo}
                    addressSearchCountryCode={`${props.policy.productCountryCode ? props.policy.productCountryCode : 'USA'}`}
                    header={<FormattedMessage id='Profile_Insured.mailingAddressDetails' />}
                    label={<FormattedMessage id='Profile_Insured.locationDetails' />}
                    saveEvent={props.doUpsertPolicy}
                    cancelEvent={props.doGetPolicy}
                    reference={props.reference}
                    changeEvent={(dotProp, value) => {
                        dotProp === 'address'
                            ? props.doReducePolicyElement({ code: 'participants.insured.party.mailingAddress', value })
                            : props.doReducePolicyElement({
                                  code: `participants.insured.party.mailingAddress.${dotProp}`,
                                  value,
                              });
                    }}
                    allowEdit={props.allowEdit}
                    address={props.policy?.participants?.insured?.party?.mailingAddress}
                />
            )}
        </React.Fragment>
    );
};

export default ProfileInsured;
